// Sơ đồ dòng tiền của một thẻ — các khoản xếp dọc đúng thứ tự máy cộng, dấu
// cộng nối giữa hai khoản, câu tóm tắt phép cộng nằm cuối.
//
// Vì sao xếp DỌC chứ không xếp ngang: một thẻ có tới sáu khoản, xếp ngang là
// chữ bị bóp lại và trên điện thoại thì tràn. Dọc thì đọc xuôi từ trên xuống
// đúng thứ tự tiền cộng vào, và mỗi khoản có đủ chỗ kể tên các ô nuôi nó.
//
// Màu theo phần lương, KHÔNG theo trạng thái tốt/xấu: đây là phân loại, không
// phải cảnh báo. Dùng lại token của app nên đổi chủ đề là sơ đồ đổi theo.

import { PAY_SECTION_LABEL, type PaySection } from "@/features/salary-rules-v2/leaf-types";
import type { PayFlow } from "./pay-flow-types";

/** Màu nền / chữ của từng phần lương. Cùng bộ token với phần còn lại của app. */
const SECTION_TONE: Record<PaySection, { bg: string; text: string; border: string }> = {
  base: { bg: "var(--color-info-bg)", text: "var(--color-info-text)", border: "var(--color-info)" },
  perWork: { bg: "var(--color-brand-glow)", text: "var(--color-brand-dark)", border: "var(--color-brand)" },
  surcharge: { bg: "var(--color-warning-bg)", text: "var(--color-warning-text)", border: "var(--color-warning)" },
  commission: { bg: "var(--color-success-bg)", text: "var(--color-success-text)", border: "var(--color-success)" },
  bonus: { bg: "var(--color-success-bg)", text: "var(--color-success-text)", border: "var(--color-success)" },
  overtime: { bg: "var(--color-danger-bg)", text: "var(--color-danger-text)", border: "var(--color-danger)" },
  // Định mức không phải tiền — tông trung tính như "Khác".
  quota: { bg: "var(--color-surface-2)", text: "var(--color-muted)", border: "var(--color-border)" },
  other: { bg: "var(--color-surface-2)", text: "var(--color-muted)", border: "var(--color-border)" },
};

export function PayFlowDiagram({ flow }: { flow: PayFlow }) {
  // Thẻ không cộng dồn (Chung, Bậc tay nghề) bày thành danh sách đánh số chứ
  // không nối bằng dấu cộng: ở đó các mục là những thứ NHÂN vào cùng một đơn
  // giá, hoặc những khoản rời chạy sang thẻ khác — vẽ dấu cộng là nói sai.
  const congDon = flow.additive !== false;

  return (
    <section className="space-y-3">
      <h3 className="font-semibold">
        {congDon ? "Tiền của thẻ này ra từ đâu" : "Thẻ này chi phối những gì"}
      </h3>

      <ol className="space-y-0">
        {flow.steps.map((step, i) => {
          const tone = SECTION_TONE[step.section];
          return (
            <li key={`${step.section}-${i}`}>
              {i > 0 && congDon && (
                <div className="flex items-center gap-2 py-1 pl-4" aria-hidden>
                  <span className="text-base font-semibold text-muted">+</span>
                  <span className="h-px flex-1 bg-border" />
                </div>
              )}
              <div
                className={`rounded-md border-l-4 p-3 ${i > 0 && !congDon ? "mt-2" : ""}`}
                style={{ background: tone.bg, borderLeftColor: tone.border }}
              >
                <p className="text-[13px] font-semibold" style={{ color: tone.text }}>
                  {i > 0 && congDon && <span className="sr-only">cộng </span>}
                  {step.title ?? PAY_SECTION_LABEL[step.section]}
                </p>
                <p className="mt-1 leading-relaxed">{step.how}</p>
                {step.fields && step.fields.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-muted">Ô trên màn:</span>
                    {step.fields.map((f) => (
                      <span
                        key={f}
                        className="rounded border border-border bg-surface px-1.5 py-0.5 text-xs"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      <p className="rounded-md border border-border bg-[var(--color-surface-2)] p-3 font-medium leading-relaxed">
        {congDon ? "= " : ""}
        {flow.sum}
      </p>

      {flow.notes && flow.notes.length > 0 && (
        <div className="rounded-md border p-3 [background:var(--color-warning-bg)] [border-color:var(--color-warning)]">
          <p className="mb-1 font-medium">Dễ hiểu nhầm ở thẻ này</p>
          <ul className="list-disc space-y-1 pl-5 text-muted">
            {flow.notes.map((n) => (
              <li key={n} className="leading-relaxed">
                {n}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
